import { Check, Copy } from "lucide-react";
import { useMemo, useState } from "react";
import { ToolDetailsPanel } from "./ToolDetailsPanel";

const formatRawValue = (value: unknown) => {
  const raw =
    typeof value === "string" ? value : (JSON.stringify(value, null, 2) ?? "");
  try {
    return { raw, display: JSON.stringify(JSON.parse(raw), null, 2) };
  } catch {
    return { raw, display: raw };
  }
};

const RawSection = ({
  label,
  value,
  waiting = false,
}: {
  label: string;
  value: unknown;
  waiting?: boolean;
}) => {
  const { raw, display } = useMemo(() => formatRawValue(value), [value]);
  const [copiedValue, setCopiedValue] = useState<string>();
  const [copyFailed, setCopyFailed] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopiedValue(raw);
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
    }
  };
  const Icon = copiedValue === raw && !copyFailed ? Check : Copy;
  return (
    <section className="min-w-0 border-0 border-t border-solid border-[#e2e8f0] px-[10px] py-[8px]">
      <div className="mb-[4px] flex h-[24px] items-center justify-between gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#6b7280]">
          {label}
        </span>
        {!waiting && (
          <button
            type="button"
            aria-label={`Copy ${label}`}
            onClick={() => void copy()}
            className="flex h-[24px] w-[24px] shrink-0 cursor-pointer items-center justify-center rounded-[3.5px] border-0 bg-transparent p-0 text-[#6b7280] hover:bg-black/[.04] focus-visible:bg-black/[.04]"
          >
            <Icon aria-hidden="true" size={14} />
          </button>
        )}
      </div>
      {waiting ? (
        <p className="m-0 text-[12px] text-[#6b7280]">Waiting for result…</p>
      ) : (
        <pre className="m-0 max-h-[96px] max-w-full overflow-auto whitespace-pre rounded-[3.5px] bg-[#f8f9fc] px-[10px] py-[8px] font-mono text-[12px] leading-[18px] text-black/87">
          {display}
        </pre>
      )}
      {copyFailed && (
        <p role="alert" className="mb-0 mt-1 text-[12px] text-[#b91c1c]">
          Could not copy {label}.
        </p>
      )}
    </section>
  );
};

export const RawToolDetails = ({
  name,
  parameters,
  result,
  waiting,
}: {
  name: string;
  parameters: unknown;
  result: unknown;
  waiting: boolean;
}) => (
  <ToolDetailsPanel label="Raw tool call">
    <header className="overflow-auto px-[10px] py-[6px] text-[12px] text-black/87">
      <code className="font-mono">{name}</code>
    </header>
    <RawSection label="parameters" value={parameters} />
    <RawSection label="result" value={result} waiting={waiting} />
  </ToolDetailsPanel>
);
